'use client';

// Root error boundary — only fires when the ROOT layout itself throws (the
// (app) route group already has its own error.tsx for normal page errors).
// Before this file existed, that case fell through to Next's bare default
// error page instead of a branded one. Must define its own <html>/<body>: it
// replaces the entire document when active, since the root layout that would
// normally provide them is what failed.
import { useEffect } from 'react';

export default function GlobalError({ error }: { error: Error & { digest?: string }; reset: () => void }) {
  useEffect(() => {
    console.error('Root layout error:', error);
    try {
      fetch('/api/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `Root error screen${error?.digest ? ` [${error.digest}]` : ''}: ${error?.message ?? 'Unknown error'}`,
          stack: error?.stack ?? null,
          url: typeof location !== 'undefined' ? location.pathname + location.search : null,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* ignore */
    }
  }, [error]);

  return (
    <html lang="en">
      <body style={{ fontFamily: 'system-ui, sans-serif', background: '#f4f6f8', margin: 0 }}>
        <div style={{ maxWidth: 560, margin: '3rem auto', padding: '2.5rem 1.5rem', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>⚠️</div>
          <h1 style={{ fontSize: '1.25rem', margin: '0 0 0.5rem' }}>Something went wrong</h1>
          <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: '1.25rem' }}>
            Please reload the page. If this keeps happening, let the team know.
            {error?.digest && (
              <>
                <br />
                Reference: <code>{error.digest}</code>
              </>
            )}
          </p>
          <a
            href="/dashboard"
            style={{
              display: 'inline-block',
              padding: '0.6rem 1.25rem',
              background: '#0f1b2d',
              color: '#fff',
              borderRadius: 6,
              textDecoration: 'none',
            }}
          >
            Back to dashboard
          </a>
        </div>
      </body>
    </html>
  );
}
